/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, inject } from '@angular/core';

import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
  StateToken,
  Store
} from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { delay, catchError, tap, finalize } from 'rxjs/operators';

import { CoverEnum } from '@app/features/home/store/products/products.model';
import { StudentState } from '@app/features/student/store/student/student.state';

import {
  extractDiscountTiersFromProducts,
  getActivePromotion,
  getTotalDiscountedPrice,
  getTotalEquivalentQuantity,
  getTotalPrice,
  MAX_INSTALLMENTS,
  MAX_QUANTITY,
  MIN_INSTALLMENT_VALUE
} from './cart-helper';
import { SetCartLoadingAction } from './cart-ui.actions';
import {
  AddToCartAction,
  RemoveFromCartAction,
  IncrementQuantityAction,
  DecrementQuantityAction,
  UpdateQuantityAction,
  ClearCartAction,
  ClearAllCartsAction,
  UpdateDiscountTiersFromProductsAction
} from './cart.action';
import {
  CartDiscountTier,
  CartItem,
  CartStateModel,
  StudentCart
} from './cart.model';

type HandleItemErrorParams = {
  ctx: StateContext<CartStateModel>;
  state: CartStateModel;
  error: Error;
  productId?: number;
};

type ErrorMapping = {
  [key: string]: {
    message: string;
    type: CartItem['error']['type'];
  };
};

export const CART_TOKEN = new StateToken<CartStateModel>('cart');

export const CART_CONFIG = {
  maxQuantity: MAX_QUANTITY,
  maxInstallments: MAX_INSTALLMENTS,
  maxCarts: 3
};

const ERROR_MAX_QUANTITY = 'Quantidade máxima atingida para este item.';
const ERROR_MIN_QUANTITY = 'Quantidade mínima atingida para este item.';

@State<CartStateModel>({
  name: CART_TOKEN,
  defaults: {
    discountTiers: [],
    carts: {}
  }
})
@Injectable()
export class CartState {
  private readonly store = inject(Store);

  private readonly errorMappings: ErrorMapping = {
    MAX_QUANTITY: {
      message: ERROR_MAX_QUANTITY,
      type: 'warning'
    },
    MIN_QUANTITY: {
      message: ERROR_MIN_QUANTITY,
      type: 'warning'
    },
    DEFAULT: {
      message: 'Erro ao atualizar item',
      type: 'danger'
    }
  };

  /**
   *
   * Métodos privados para manipulação do estado do carrinho
   */

  private getCurrentStudentId(): number {
    const student = this.store.selectSnapshot(StudentState.student);
    if (!student?.id) {
      throw new Error('Nenhum aluno selecionado');
    }
    return student.id;
  }

  private getCurrentCart(ctx: StateContext<CartStateModel>): StudentCart {
    const studentId = this.getCurrentStudentId();
    const state = ctx.getState();

    if (!state.carts?.[studentId]) {
      this.createNewCart(ctx, studentId);
    }

    return state.carts?.[studentId] || this.getEmptyCart();
  }

  private getEmptyCart(): StudentCart {
    return {
      items: [],
      lastAccessed: Date.now()
    };
  }

  private createNewCart(
    ctx: StateContext<CartStateModel>,
    studentId: number
  ): void {
    const state = ctx.getState();
    const currentStudentId = this.getCurrentStudentId();

    if (studentId !== currentStudentId) {
      this.enforceCartLimit(ctx);
    }

    ctx.patchState({
      carts: {
        ...state.carts,
        [studentId]: this.getEmptyCart()
      }
    });
  }

  private enforceCartLimit(ctx: StateContext<CartStateModel>): void {
    const { carts } = ctx.getState();
    const currentStudentId = this.getCurrentStudentId();

    const removableCarts = Object.entries(carts).filter(
      ([id]) => Number(id) !== currentStudentId
    );

    if (removableCarts.length >= CART_CONFIG.maxCarts) {
      const [oldestId, _] = removableCarts.reduce((oldest, current) =>
        current[1].lastAccessed < oldest[1].lastAccessed ? current : oldest
      );

      const updatedCarts = { ...carts };
      delete updatedCarts[oldestId];

      ctx.patchState({ carts: updatedCarts });
    }
  }

  private updateCurrentCart(
    ctx: StateContext<CartStateModel>,
    items: CartItem[]
  ): void {
    const studentId = this.getCurrentStudentId();
    const state = ctx.getState();

    ctx.patchState({
      carts: {
        ...state.carts,
        [studentId]: {
          items: this.applyDiscounts(items, state.discountTiers),
          lastAccessed: Date.now()
        }
      }
    });

    this.enforceCartLimit(ctx);
  }

  private simulateRequest(): Observable<void> {
    const randomDelay = Math.floor(Math.random() * 750) + 225;
    return of(null).pipe(delay(randomDelay));
  }

  private createError(code: string): CartItem['error'] {
    const { message, type } =
      this.errorMappings[code] || this.errorMappings['DEFAULT'];

    return {
      message,
      code,
      type,
      timestamp: Date.now()
    };
  }

  private handleItemError(params: HandleItemErrorParams): void {
    const { ctx, state, error, productId } = params;

    const errorDetails = this.createError(error?.message || 'DEFAULT');

    if (productId) {
      const studentId = this.getCurrentStudentId();
      const currentCart = state.carts[studentId] || this.getEmptyCart();

      const updatedItems = currentCart.items.map((item) =>
        item.productId === productId ? { ...item, error: errorDetails } : item
      );

      ctx.patchState({
        carts: {
          ...state.carts,
          [studentId]: {
            ...currentCart,
            items: updatedItems
          }
        }
      });
    }

    ctx.dispatch(new SetCartLoadingAction(false));
  }

  /**
   *
   * Selectors para acessar o estado do carrinho
   */

  @Selector()
  static discountTiers({ discountTiers }: CartStateModel) {
    return discountTiers || [];
  }

  @Selector() static minQuantityForKit({
    discountTiers
  }: CartStateModel): number {
    return (
      discountTiers.sort((a, b) => a.minQuantity - b.minQuantity)[0]
        ?.minQuantity ?? 4 // Default fallback if no tiers exist
    );
  }

  static cartItems(studentId?: number) {
    return createSelector(
      [CartState, StudentState.student],
      (state: CartStateModel, student) => {
        const targetStudentId = studentId ?? student?.id;
        if (!targetStudentId || !state.carts?.[targetStudentId]) {
          return [];
        }
        return (
          state.carts?.[targetStudentId]?.items?.map((item) => ({
            ...item,
            originalPrice: item.originalPrice || item.price,
            totalValue: item.totalValue || item.price * item.quantity
          })) || []
        );
      }
    );
  }

  static itemsWithErrors(studentId?: number) {
    return createSelector([CartState.cartItems(studentId)], (items) =>
      items.filter((item) => item.error)
    );
  }

  static hasErrors(studentId?: number) {
    return createSelector([CartState.cartItems(studentId)], (items) =>
      items.some((item) => item.error)
    );
  }

  static subtotal(studentId?: number) {
    return createSelector([CartState.cartItems(studentId)], (items) =>
      getTotalDiscountedPrice(items)
    );
  }

  static shippingCost(studentId?: number) {
    return createSelector([CartState.cartItems(studentId)], () => 0);
  }

  static uniqueItemsCount(studentId?: number) {
    return createSelector(
      [CartState.cartItems(studentId)],
      (items) => items?.length || 0
    );
  }

  static totalItemsCount(studentId?: number) {
    return createSelector(
      [CartState.cartItems(studentId)],
      (items) => items?.reduce((acc, item) => acc + item.quantity, 0) || 0
    );
  }

  static installmentOptions(studentId?: number) {
    return createSelector([CartState.cartItems(studentId)], (items) => {
      const total = getTotalDiscountedPrice(items);
      const possibleInstallments = Math.floor(total / MIN_INSTALLMENT_VALUE);
      const installments = Math.min(possibleInstallments, MAX_INSTALLMENTS);
      const finalInstallments = installments >= 1 ? installments : 1;

      return {
        installments: finalInstallments,
        price:
          total > 0 ? parseFloat((total / finalInstallments).toFixed(2)) : 0
      };
    });
  }

  static isCartEmpty(studentId?: number) {
    return createSelector(
      [CartState.cartItems(studentId)],
      (items) => items.length === 0
    );
  }

  static getCartItemById(studentId?: number) {
    return createSelector(
      [CartState.cartItems(studentId)],
      (items) => (id: number) => items.find((item) => item.productId === id)
    );
  }

  static cartSummary(studentId?: number) {
    return createSelector(
      [CartState.cartItems(studentId), CartState.discountTiers],
      (items, discountTiers) => {
        const promotion = getActivePromotion(items, discountTiers);
        if (!promotion) {
          return null;
        }

        return {
          totalPrice: getTotalPrice(items),
          totalDiscountedPrice: getTotalDiscountedPrice(items),
          promotion: {
            title: promotion.title,
            description: promotion.description
          }
        };
      }
    );
  }

  /**
   *
   * Actions para manipulação do estado do carrinho
   * Essas ações são responsáveis por atualizar o estado do carrinho
   */

  @Action(UpdateDiscountTiersFromProductsAction)
  updateDiscountTiers(
    ctx: StateContext<CartStateModel>,
    action: UpdateDiscountTiersFromProductsAction
  ) {
    const tiers = extractDiscountTiersFromProducts(action.products);
    ctx.patchState({
      discountTiers: tiers
    });
  }

  @Action(UpdateQuantityAction)
  updateQuantity(
    ctx: StateContext<CartStateModel>,
    action: UpdateQuantityAction
  ): Observable<void> {
    const currentCart = this.getCurrentCart(ctx);
    ctx.dispatch(new SetCartLoadingAction(true));

    return this.simulateRequest().pipe(
      tap(() => {
        try {
          const updatedItems = currentCart.items.map((item) => {
            if (item.productId === action.payload.id) {
              if (action.payload.quantity > (item.stock ?? MAX_QUANTITY)) {
                throw new Error('MAX_QUANTITY');
              }
              if (action.payload.quantity < 1) {
                throw new Error('MIN_QUANTITY');
              }

              return {
                ...item,
                quantity: action.payload.quantity,
                error: null
              };
            }
            return item;
          });

          this.updateCurrentCart(
            ctx,
            this.applyDiscounts(updatedItems, ctx.getState().discountTiers)
          );
          ctx.dispatch(new SetCartLoadingAction(false));
        } catch (error) {
          this.handleItemError({
            ctx,
            state: ctx.getState(),
            error,
            productId: action.payload.id
          });
        }
      }),
      catchError((error) => {
        this.handleItemError({
          ctx,
          state: ctx.getState(),
          error,
          productId: action.payload.id
        });
        return of(undefined);
      })
    );
  }

  @Action(AddToCartAction)
  addToCart(
    ctx: StateContext<CartStateModel>,
    action: AddToCartAction
  ): Observable<void> {
    const currentCart = this.getCurrentCart(ctx);
    ctx.dispatch(new SetCartLoadingAction(true));

    return this.simulateRequest().pipe(
      tap(() => {
        try {
          const itemToAdd = {
            ...action.item,
            totalValue: parseFloat(
              (action.item.originalPrice * (action.item.quantity || 1)).toFixed(
                2
              )
            ),
            discount: null,
            error: null
          };

          const existing = currentCart.items.find(
            (item) => item.productId === itemToAdd.productId
          );

          let newItems: CartItem[] = [];

          if (existing) {
            const newQuantity = existing.quantity + itemToAdd.quantity;
            const maxQuantity = existing.stock ?? MAX_QUANTITY;

            if (newQuantity > maxQuantity) {
              // Em vez de lançar erro, ajustamos para a quantidade máxima
              newItems = currentCart.items.map((item) => {
                if (item.productId === existing.productId) {
                  return {
                    ...item,
                    quantity: maxQuantity,
                    originalPrice: item.originalPrice || item.price,
                    error: this.createError('MAX_QUANTITY')
                  };
                }
                return item;
              });
            } else {
              newItems = currentCart.items.map((item) => {
                if (item.productId === existing.productId) {
                  return {
                    ...item,
                    quantity: newQuantity,
                    originalPrice: item.originalPrice || item.price,
                    error: null
                  };
                }
                return item;
              });
            }
          } else {
            const maxQuantity = itemToAdd.stock ?? MAX_QUANTITY;

            if (itemToAdd.quantity > maxQuantity) {
              newItems = [
                {
                  ...itemToAdd,
                  quantity: maxQuantity,
                  error: this.createError('MAX_QUANTITY')
                },
                ...currentCart.items
              ];
            } else {
              newItems = [itemToAdd, ...currentCart.items];
            }
          }

          this.updateCurrentCart(
            ctx,
            this.applyDiscounts(newItems, ctx.getState().discountTiers)
          );
          ctx.dispatch(new SetCartLoadingAction(false));
        } catch (error) {
          this.handleItemError({
            ctx,
            state: ctx.getState(),
            error,
            productId: action.item.productId
          });
        }
      }),
      catchError((error) => {
        this.handleItemError({
          ctx,
          state: ctx.getState(),
          error,
          productId: action.item.productId
        });
        return of(undefined);
      })
    );
  }

  @Action(RemoveFromCartAction)
  removeFromCart(
    ctx: StateContext<CartStateModel>,
    action: RemoveFromCartAction
  ): Observable<void> {
    const currentCart = this.getCurrentCart(ctx);
    ctx.dispatch(new SetCartLoadingAction(true));

    return this.simulateRequest().pipe(
      tap(() => {
        const filtered = currentCart.items.filter(
          (item) => item.productId !== action.id
        );

        this.updateCurrentCart(
          ctx,
          this.applyDiscounts(filtered, ctx.getState().discountTiers)
        );
        ctx.dispatch(new SetCartLoadingAction(false));
      }),
      catchError((error) => {
        this.handleItemError({
          ctx,
          state: ctx.getState(),
          error
        });
        return of(undefined);
      })
    );
  }

  @Action(IncrementQuantityAction)
  incrementQuantity(
    ctx: StateContext<CartStateModel>,
    action: IncrementQuantityAction
  ): Observable<void> {
    const currentCart = this.getCurrentCart(ctx);
    ctx.dispatch(new SetCartLoadingAction(true));

    return this.simulateRequest().pipe(
      tap(() => {
        try {
          const updatedItems = currentCart.items.map((item) => {
            if (item.productId === action.id) {
              if (item.quantity >= (item.stock ?? MAX_QUANTITY)) {
                throw new Error('MAX_QUANTITY');
              }

              return {
                ...item,
                quantity: item.quantity + 1,
                error: null
              };
            }
            return item;
          });

          this.updateCurrentCart(
            ctx,
            this.applyDiscounts(updatedItems, ctx.getState().discountTiers)
          );
          ctx.dispatch(new SetCartLoadingAction(false));
        } catch (error) {
          this.handleItemError({
            ctx,
            state: ctx.getState(),
            error,
            productId: action.id
          });
        }
      }),
      catchError((error) => {
        this.handleItemError({
          ctx,
          state: ctx.getState(),
          error,
          productId: action.id
        });
        return of(undefined);
      })
    );
  }

  @Action(DecrementQuantityAction)
  decrementQuantity(
    ctx: StateContext<CartStateModel>,
    action: DecrementQuantityAction
  ): Observable<void> {
    const currentCart = this.getCurrentCart(ctx);
    ctx.dispatch(new SetCartLoadingAction(true));

    return this.simulateRequest().pipe(
      tap(() => {
        const itemToUpdate = currentCart.items.find(
          (item) => item.productId === action.id
        );

        if (!itemToUpdate) {
          ctx.dispatch(new SetCartLoadingAction(false));
          return;
        }

        // Se quantidade for 1, remove o item completamente
        if (itemToUpdate.quantity <= 1) {
          const filtered = currentCart.items.filter(
            (item) => item.productId !== action.id
          );
          this.updateCurrentCart(
            ctx,
            this.applyDiscounts(filtered, ctx.getState().discountTiers)
          );
          ctx.dispatch(new SetCartLoadingAction(false));
          return;
        }

        // Atualiza a quantidade normalmente
        const updatedItems = currentCart.items.map((item) => {
          if (item.productId === action.id) {
            return {
              ...item,
              quantity: item.quantity - 1,
              error: null
            };
          }
          return item;
        });

        this.updateCurrentCart(
          ctx,
          this.applyDiscounts(updatedItems, ctx.getState().discountTiers)
        );
        ctx.dispatch(new SetCartLoadingAction(false));
      }),
      catchError(() => {
        ctx.dispatch(new SetCartLoadingAction(false));
        return of(undefined);
      })
    );
  }

  @Action(ClearCartAction)
  clearCart(ctx: StateContext<CartStateModel>, action: ClearCartAction) {
    const studentId = action.studentId ?? this.getCurrentStudentId();

    if (!studentId) {
      console.warn('No student ID provided for clearing cart');
      return of(undefined);
    }

    ctx.dispatch(new SetCartLoadingAction(true));

    return this.simulateRequest().pipe(
      tap(() => {
        const state = ctx.getState();

        ctx.patchState({
          carts: {
            ...state.carts,
            [studentId]: { items: [], lastAccessed: Date.now() }
          }
        });
      }),
      finalize(() => {
        ctx.dispatch(new SetCartLoadingAction(false));
      })
    );
  }

  @Action(ClearAllCartsAction)
  async resetAllCarts(ctx: StateContext<CartStateModel>) {
    ctx.dispatch(new SetCartLoadingAction(true));

    return this.simulateRequest().pipe(
      tap(() => {
        ctx.patchState({ carts: {} });
      }),
      finalize(() => {
        ctx.dispatch(new SetCartLoadingAction(false));
      })
    );
  }

  /**
   *
   * Métodos privados para aplicar descontos e calcular totais
   */

  private applyDiscounts(
    items: CartItem[],
    discountTiers: CartDiscountTier[]
  ): CartItem[] {
    const promotion = getActivePromotion(items, discountTiers);

    if (!promotion) {
      return items.map((item) => ({
        ...item,
        originalPrice: item.originalPrice || item.price,
        price: item.isKit
          ? item.price || item.originalPrice
          : item.originalPrice || item.price,
        discount: null,
        totalValue: parseFloat(
          ((item.originalPrice || item.price) * item.quantity).toFixed(2)
        )
      }));
    }

    // Encontra o melhor tier aplicável considerando todos os itens
    const bestTierByCoverType = items.reduce(
      (acc, item) => {
        if (!acc[item.coverType]) {
          const coverItems = items.filter(
            (i) => i.coverType === item.coverType
          );
          const totalQuantity = getTotalEquivalentQuantity(coverItems);

          const applicableTier = [...discountTiers]
            .sort((a, b) => b.minQuantity - a.minQuantity)
            .find(
              (tier) =>
                totalQuantity >= tier.minQuantity &&
                tier.coverType === item.coverType
            );

          if (applicableTier) {
            acc[item.coverType] = applicableTier;
          }
        }
        return acc;
      },
      {} as Record<CoverEnum, CartDiscountTier>
    );

    return items.map((item) => {
      const applicableTier = bestTierByCoverType[item.coverType];

      if (!applicableTier) {
        return {
          ...item,
          discount: null,
          totalValue: parseFloat(
            ((item.originalPrice || item.price) * item.quantity).toFixed(2)
          )
        };
      }

      const originalPrice = item.originalPrice ?? item.price;
      const newPrice = applicableTier.price;

      if (item.isKit) {
        const kitSize = item.kitSize || 1;
        const kitPrice = newPrice * kitSize;
        const originalKitPrice = originalPrice;

        return {
          ...item,
          price: parseFloat(kitPrice.toFixed(2)),
          discount: {
            formattedPercentage: applicableTier.formattedPercentage,
            percentage: applicableTier.percentage,
            originalTotalPrice: parseFloat(
              (originalKitPrice * item.quantity).toFixed(2)
            ),
            discountedPrice: parseFloat((kitPrice * item.quantity).toFixed(2)),
            promotion,
            discountType: 'KIT'
          },
          totalValue: parseFloat((kitPrice * item.quantity).toFixed(2))
        };
      }

      return {
        ...item,
        price: parseFloat(newPrice.toFixed(2)),
        discount: {
          formattedPercentage: applicableTier.formattedPercentage,
          percentage: applicableTier.percentage,
          originalTotalPrice: parseFloat(
            (originalPrice * item.quantity).toFixed(2)
          ),
          discountedPrice: parseFloat((newPrice * item.quantity).toFixed(2)),
          promotion,
          discountType: 'UNIT'
        },
        totalValue: parseFloat((newPrice * item.quantity).toFixed(2))
      };
    });
  }
}
